import { zeilenId } from "./zeilen";

/**
 * Vertrag zwischen PDF-Editor und Server für Bearbeitungen, die nur der Server
 * kann (echt entfernen, Text ersetzen) — identisch in
 * `apps/api/src/pdf-bearbeitung/pdf-bearbeitung.types.ts`.
 *
 * Koordinaten: PDF-Userspace (pt, Ursprung unten links), Seiten 0-basiert.
 */
export type PdfRect = [number, number, number, number];

export interface PdfEntfernen {
    seite: number;
    rect: PdfRect;
}

export interface PdfTextErsetzen {
    seite: number;
    /** Bereich der alten Zeile — wird echt entfernt. */
    rect: PdfRect;
    /** Neuer Text ('' = Zeile nur entfernen). */
    text: string;
    /** Grundlinie Start (Userspace). */
    x: number;
    y: number;
    /** Schriftgröße in pt. */
    groesse: number;
    schrift: "sans" | "serif" | "mono";
    fett: boolean;
    drehung: 0 | 90 | 180 | 270;
}

/**
 * Ausschneiden & Verschieben (Teilplan 06 B): der Ausschnitt geht als PNG mit
 * (Multipart-Liste „bilder“) und wird an `rect` in den Seiteninhalt gezeichnet.
 * Seine Quelle steht als gewöhnlicher Eintrag in `entfernen`.
 */
export interface PdfEinfuegen {
    /** Zielseite, 0-basiert. */
    seite: number;
    /** Zielrechteck im Userspace. */
    rect: PdfRect;
    /** Index in die Multipart-Dateiliste „bilder“. */
    bild: number;
}

export interface PdfBearbeitung {
    entfernen: PdfEntfernen[];
    ersetzen: PdfTextErsetzen[];
    einfuegen: PdfEinfuegen[];
    /**
     * Quellen verschobener Ausschnitte — der Server entfernt sie wie `entfernen`,
     * lässt aber Unterschriften, Stempel und Formularfelder an ihrem Platz.
     */
    ausschnittQuellen?: PdfEntfernen[];
    /**
     * Schwärzen (T-363): wie `entfernen` echt aus der Datei, danach ein
     * schwarzer Balken an der Stelle. Nur gesetzt, wenn es etwas zu schwärzen gibt.
     */
    schwaerzen?: PdfEntfernen[];
}

export function istLeer(b: PdfBearbeitung): boolean {
    return (
        b.entfernen.length === 0 &&
        b.ersetzen.length === 0 &&
        (b.einfuegen?.length ?? 0) === 0 &&
        (b.ausschnittQuellen?.length ?? 0) === 0 &&
        (b.schwaerzen?.length ?? 0) === 0
    );
}

// ─── Zustand im Editor ──────────────────────────────────────────────────────

/** Eine zum Entfernen markierte Stelle — mit Id fürs Zurücknehmen. */
export type Markierung = PdfEntfernen & {
    id: string;
    /**
     * Aufgezogenes Rechteck, Textauswahl — oder die Quelle eines Ausschnitts
     * (Teilplan 06 B; `gruppe` = Id des Ausschnitts, gezeichnet von der
     * Ausschnitt-Ebene, nicht als rote Schraffur).
     */
    quelle: "rechteck" | "text" | "ausschnitt";
    /** Ausgewählter Text (nur Anzeige/Ausschneiden, geht nicht an den Server). */
    text?: string;
    /**
     * Eine mehrzeilige Textauswahl ergibt mehrere Rechtecke — sie teilen sich
     * eine Gruppe, damit ein × die ganze Auswahl zurücknimmt.
     */
    gruppe?: string;
    /**
     * Schwärzen statt Entfernen (T-363): der Server entfernt genauso echt, legt
     * aber einen schwarzen Balken darüber. Geht als `schwaerzen` an den Server.
     */
    schwarz?: boolean;
};

/** Farben fürs Durchstreichen (T-363) — Rot ist Standard. */
export type StreichFarbe = "rot" | "schwarz" | "blau";

export const STREICH_FARBEN: Record<StreichFarbe, { name: string; rgb: [number, number, number]; css: string }> = {
    rot: { name: "Rot", rgb: [0.85, 0.05, 0.05], css: "rgb(217, 13, 13)" },
    schwarz: { name: "Schwarz", rgb: [0, 0, 0], css: "rgb(0, 0, 0)" },
    blau: { name: "Blau", rgb: [0.05, 0.25, 0.8], css: "rgb(13, 64, 204)" },
};

/**
 * Eine Durchstreichung (T-363): ein Strich durch Text, der lesbar bleibt.
 * Geht NICHT an den Server — der Editor schreibt sie beim Speichern selbst als
 * `/StrikeOut`-Anmerkung in die Datei (`streichungSchreiben.ts`).
 */
export interface StreichEintrag {
    id: string;
    seite: number;
    /** Umriss (Userspace) — Rechteck der Anmerkung und ihrer QuadPoints. */
    rect: PdfRect;
    /** Der Strich selbst: Anfang und Ende im Userspace [x1, y1, x2, y2]. */
    linie: [number, number, number, number];
    /** Strichstärke in pt. */
    staerke: number;
    farbe: StreichFarbe;
    /** Textauswahl oder (bei Scans) aufgezogene Linie. */
    quelle: "text" | "linie";
    /** Eine mehrzeilige Auswahl = eine Gruppe, ein × nimmt alle zurück. */
    gruppe?: string;
}

export type ErsetzenEintrag = PdfTextErsetzen & { id: string };

/** Ein ausgeschnittenes, bewegliches Stück (Teilplan 06 B). */
export interface AusschnittEintrag {
    id: string;
    /** Aktuelle Zielseite und -stelle (Userspace). */
    seite: number;
    rect: PdfRect;
    /** Verbindet mit der Quell-Markierung in `entfernen`. */
    gruppe: string;
    /** Object-URL des aufgenommenen Bildes — `null`, solange es noch aufgenommen wird. */
    bildUrl: string | null;
    blob: Blob | null;
}

export type BearbeitungAktion =
    | { typ: "entfernenHinzu"; markierungen: Markierung[] }
    | { typ: "entfernenWeg"; id: string }
    | { typ: "ersetzenSetzen"; eintrag: ErsetzenEintrag } // Teilplan 05
    | { typ: "ersetzenWeg"; id: string } // Teilplan 05
    | { typ: "ersetzen"; stand: BearbeitungStand }
    /** Quelle markieren und bewegliches Element anlegen — ein Schritt. */
    | { typ: "ausschneiden"; markierung: Markierung; ausschnitt: AusschnittEintrag }
    /** Nach Gruppe, nicht Id: ein Seitenumbau während der Aufnahme benennt Ids um. */
    | { typ: "ausschnittBild"; gruppe: string; bildUrl: string; blob: Blob }
    | { typ: "ausschnittVerschieben"; id: string; seite: number; rect: PdfRect }
    /** Element weg UND Quelle zurück — Ausschneiden rückgängig. */
    | { typ: "ausschnittWeg"; id: string }
    /** Nach dem Seiten-Organisator — siehe `nachSeitenumbau`. */
    | { typ: "seitenumbau"; eintraege: readonly { quelle: string; seite: number }[]; haupt?: string }
    /** Durchstreichen (T-363). */
    | { typ: "streichenHinzu"; eintraege: StreichEintrag[] }
    /** Nimmt den Eintrag zurück — samt allen Einträgen seiner Gruppe. */
    | { typ: "streichenWeg"; id: string }
    | { typ: "leeren" };

export interface BearbeitungStand {
    entfernen: Markierung[];
    ersetzen: ErsetzenEintrag[];
    einfuegen: AusschnittEintrag[];
    /** Durchstreichungen (T-363) — schreibt der Editor selbst, nicht der Server. */
    streichen: StreichEintrag[];
}

export const LEERER_STAND: BearbeitungStand = { entfernen: [], ersetzen: [], einfuegen: [], streichen: [] };

export function bearbeitungReducer(s: BearbeitungStand, a: BearbeitungAktion): BearbeitungStand {
    switch (a.typ) {
        case "entfernenHinzu":
            return a.markierungen.length ? { ...s, entfernen: [...s.entfernen, ...a.markierungen] } : s;
        case "entfernenWeg":
            return { ...s, entfernen: s.entfernen.filter((m) => m.id !== a.id) };
        case "ersetzenSetzen": {
            const ohne = s.ersetzen.filter((e) => e.id !== a.eintrag.id);
            return { ...s, ersetzen: [...ohne, a.eintrag] };
        }
        case "ersetzenWeg":
            return { ...s, ersetzen: s.ersetzen.filter((e) => e.id !== a.id) };
        case "ersetzen":
            return a.stand;
        case "ausschneiden":
            return { ...s, entfernen: [...s.entfernen, a.markierung], einfuegen: [...s.einfuegen, a.ausschnitt] };
        case "ausschnittBild":
            return {
                ...s,
                einfuegen: s.einfuegen.map((e) =>
                    gehoertZuGruppe(e.gruppe, a.gruppe) ? { ...e, bildUrl: a.bildUrl, blob: a.blob } : e,
                ),
            };
        case "ausschnittVerschieben":
            return {
                ...s,
                einfuegen: s.einfuegen.map((e) => (e.id === a.id ? { ...e, seite: a.seite, rect: a.rect } : e)),
            };
        case "ausschnittWeg": {
            const weg = s.einfuegen.find((e) => e.id === a.id);
            if (!weg) return s;
            const einfuegen = s.einfuegen.filter((e) => e.id !== a.id);
            // Die Quelle nur zurücknehmen, wenn kein anderer Ausschnitt (Kopie
            // nach Seitenumbau) sie noch braucht.
            const nochGebraucht = einfuegen.some((e) => e.gruppe === weg.gruppe);
            const entfernen = nochGebraucht
                ? s.entfernen
                : s.entfernen.filter((m) => !(m.quelle === "ausschnitt" && m.gruppe === weg.gruppe));
            return { ...s, entfernen, einfuegen };
        }
        case "seitenumbau":
            return nachSeitenumbau(s, a.eintraege, a.haupt);
        case "streichenHinzu":
            return a.eintraege.length ? { ...s, streichen: [...s.streichen, ...a.eintraege] } : s;
        case "streichenWeg": {
            const weg = s.streichen.find((e) => e.id === a.id);
            if (!weg) return s;
            return {
                ...s,
                streichen: s.streichen.filter((e) => e.id !== a.id && !(weg.gruppe && e.gruppe === weg.gruppe)),
            };
        }
        case "leeren":
            return LEERER_STAND;
    }
}

/** Gleiche Gruppe — oder deren Kopie aus einem Seitenumbau (`gruppe~n`). */
const gehoertZuGruppe = (gruppe: string, gesucht: string) => gruppe === gesucht || gruppe.startsWith(`${gesucht}~`);

/** Unter so viel pt Abweichung gilt ein Ausschnitt als nicht verschoben. */
const UNVERSCHOBEN_PT = 0.5;

/**
 * Wurde der Ausschnitt wirklich bewegt? Liegt er (fast) genau auf seiner Quelle,
 * wäre „entfernen und als Bild wieder hinlegen“ nur ein Qualitätsverlust
 * (Text würde zum Bild) — er fällt dann samt Quelle aus dem Auftrag.
 */
function istVerschoben(s: BearbeitungStand, a: AusschnittEintrag): boolean {
    const quelle = s.entfernen.find((m) => m.quelle === "ausschnitt" && m.gruppe === a.gruppe);
    if (!quelle || quelle.seite !== a.seite) return true;
    return a.rect.some((v, i) => Math.abs(v - quelle.rect[i]) > UNVERSCHOBEN_PT);
}

/** Die Ausschnitte, die tatsächlich verschoben werden — in Auftragsreihenfolge. */
export function verschobeneAusschnitte(s: BearbeitungStand): AusschnittEintrag[] {
    return s.einfuegen.filter((a) => istVerschoben(s, a));
}

/** Nur der Vertrag — ohne Ids und Anzeige-Felder. */
export function alsAuftrag(s: BearbeitungStand): PdfBearbeitung {
    const verschoben = verschobeneAusschnitte(s);
    const gebraucht = new Set(verschoben.map((a) => a.gruppe));
    const kopie = ({ seite, rect }: PdfEntfernen): PdfEntfernen => ({ seite, rect: [...rect] as PdfRect });
    const schwaerzen = s.entfernen.filter((m) => m.schwarz && m.quelle !== "ausschnitt").map(kopie);
    return {
        entfernen: s.entfernen.filter((m) => m.quelle !== "ausschnitt" && !m.schwarz).map(kopie),
        ersetzen: s.ersetzen.map(({ id: _id, ...rest }) => ({ ...rest, rect: [...rest.rect] as PdfRect })),
        // `bild` = Position in `ausschnittBilder(s)` — dieselbe Reihenfolge.
        einfuegen: verschoben.map(({ seite, rect }, bild) => ({ seite, rect: [...rect] as PdfRect, bild })),
        ausschnittQuellen: s.entfernen
            .filter((m) => m.quelle === "ausschnitt" && gebraucht.has(m.gruppe ?? ""))
            .map(kopie),
        // Nur wenn es etwas zu schwärzen gibt — der Vertrag bleibt sonst wie bisher.
        ...(schwaerzen.length ? { schwaerzen } : {}),
    };
}

/** Die Bilder zum Auftrag, in derselben Reihenfolge wie `alsAuftrag(s).einfuegen`; `null` = noch nicht aufgenommen. */
export function ausschnittBilder(s: BearbeitungStand): (Blob | null)[] {
    return verschobeneAusschnitte(s).map((e) => e.blob);
}

/**
 * Nach dem Seiten-Organisator (Teilplan 02): neue Seite `i` stammt aus
 * `eintraege[i]`. Stammt sie aus dem bearbeiteten Dokument selbst (`haupt`,
 * = `seitenOps.HAUPT`), wandern die Einträge ihrer alten Seite mit; Einträge
 * gelöschter Seiten entfallen, eingefügte fremde Seiten tragen keine.
 *
 * Eine Drehung durch den Organisator ändert nur `/Rotate` — der Userspace und
 * damit die Rechtecke bleiben gültig.
 */
export function nachSeitenumbau(
    s: BearbeitungStand,
    eintraege: readonly { quelle: string; seite: number }[],
    haupt = "haupt",
): BearbeitungStand {
    const entfernen: Markierung[] = [];
    const ersetzen: ErsetzenEintrag[] = [];
    const einfuegen: AusschnittEintrag[] = [];
    const streichen: StreichEintrag[] = [];
    const schonBenutzt = new Set<number>();
    eintraege.forEach((e, neu) => {
        if (e.quelle !== haupt) return;
        // Dieselbe alte Seite zweimal übernommen → Kopie mit eigener Id.
        const kopie = schonBenutzt.has(e.seite);
        schonBenutzt.add(e.seite);
        const id = (alt: string) => (kopie ? `${alt}~${neu}` : alt);
        for (const m of s.entfernen) {
            if (m.seite !== e.seite) continue;
            const gruppe = m.gruppe && kopie ? id(m.gruppe) : m.gruppe;
            entfernen.push({ ...m, id: id(m.id), seite: neu, ...(gruppe ? { gruppe } : {}) });
        }
        // Geänderte Zeilen: Id wie `zeilenId` aus der NEUEN Seite — sonst fände die
        // Text-Ebene die Änderung beim erneuten Anklicken der Zeile nicht wieder.
        for (const t of s.ersetzen) if (t.seite === e.seite) ersetzen.push({ ...t, id: zeilenId({ ...t, seite: neu }), seite: neu });
        // Ausschnitte folgen ihrer ZIELseite. Auf einer Kopie heißt die Gruppe
        // wie die der mitkopierten Quelle, wenn die Quelle auf derselben Seite
        // liegt — sonst teilt die Kopie die eine Quelle des Originals.
        for (const a of s.einfuegen) {
            if (a.seite !== e.seite) continue;
            const quelleHier = s.entfernen.some(
                (m) => m.quelle === "ausschnitt" && m.gruppe === a.gruppe && m.seite === e.seite,
            );
            einfuegen.push({ ...a, id: id(a.id), gruppe: quelleHier ? id(a.gruppe) : a.gruppe, seite: neu });
        }
        // Durchstreichungen wandern mit ihrer Seite (Userspace bleibt gültig).
        for (const st of s.streichen) {
            if (st.seite !== e.seite) continue;
            const gruppe = st.gruppe && kopie ? id(st.gruppe) : st.gruppe;
            streichen.push({ ...st, id: id(st.id), seite: neu, ...(gruppe ? { gruppe } : {}) });
        }
    });
    // Ausschnitt auf einer gelöschten Seite verworfen → seine Quelle bleibt stehen
    // (Ausschneiden rückgängig), statt still zu verschwinden.
    const lebendeGruppen = new Set(einfuegen.map((a) => a.gruppe));
    const ohneWaisen = entfernen.filter((m) => m.quelle !== "ausschnitt" || lebendeGruppen.has(m.gruppe ?? ""));
    return { entfernen: ohneWaisen, ersetzen, einfuegen, streichen };
}

/** Anzahl der Stellen, die der Nutzer markiert hat (eine Textauswahl = eine Stelle). */
export function anzahlBereiche(s: BearbeitungStand): number {
    return new Set(s.entfernen.filter((m) => m.quelle !== "ausschnitt" && !m.schwarz).map((m) => m.gruppe ?? m.id)).size;
}

/** Anzahl der geschwärzten Stellen (T-363; eine Textauswahl = eine Stelle). */
export function anzahlGeschwaerzt(s: BearbeitungStand): number {
    return new Set(s.entfernen.filter((m) => m.quelle !== "ausschnitt" && m.schwarz).map((m) => m.gruppe ?? m.id)).size;
}

/** Anzahl der durchgestrichenen Stellen (T-363; eine Textauswahl = eine Stelle). */
export function anzahlStreichungen(s: BearbeitungStand): number {
    return new Set(s.streichen.map((e) => e.gruppe ?? e.id)).size;
}

/** Anzahl der tatsächlich verschobenen Ausschnitte. */
export function anzahlAusschnitte(s: BearbeitungStand): number {
    return verschobeneAusschnitte(s).length;
}
